import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileCheck2, Globe, Layers, ListOrdered, Sparkles } from "lucide-react";
import { api, getUser, type Conversion, type PlanSummary, type Priority, type Site, reviewState } from "../api";
import { PriorityPill, writePlanSite } from "../components/planMeta";
import { PageHeader } from "../components/Page";

export function Dashboard() {
  const user = getUser();
  const isSuper = user?.role === "superadmin";
  const [conversions, setConversions] = useState<Conversion[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.listConversions({ limit: 200 }), api.listSites()])
      .then(([c, s]) => {
        setConversions(c.items);
        setSites(s);
      })
      .finally(() => setLoading(false));
  }, []);

  const succeeded = conversions.filter((c) => c.status === "succeeded" || c.status === "degraded").length;
  const inflight = conversions.filter((c) => c.status === "queued" || c.status === "running").length;
  const totalForms = conversions.reduce((sum, c) => sum + (c.forms_detected || 0), 0);
  const needsReview = conversions.filter((c) => reviewState(c) === "review").length;

  return (
    <div>
      <PageHeader title="Dashboard" description="Overview of your accessibility program." />

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <Stat icon={Layers} label="Documents" value={conversions.length} />
        <Stat icon={FileCheck2} label="Converted" value={succeeded} sub={`${inflight} in flight · ${needsReview} need review`} />
        <Stat icon={Sparkles} label="Forms detected" value={totalForms} />
        <Stat icon={Globe} label="Sites" value={sites.length} sub={`${sites.filter((s) => s.status === "ready").length} ready`} />
      </section>

      <PlanOverview sites={sites} />

      <section className="card mb-6">
        <div className="card-header">
          <h2 className="text-base font-semibold text-slate-800">Recent conversions</h2>
          <Link to="/workbench" className="text-sm text-brand-600 hover:underline">View all</Link>
        </div>
        <div className="card-body">
          {loading ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : conversions.length === 0 ? (
            <p className="text-sm text-slate-500">No conversions yet — upload a PDF on the Workbench or add a Site to start crawling.</p>
          ) : (
            <ul className="divide-y divide-slate-200">
              {conversions.slice(0, 5).map((c) => (
                <li key={c.id} className="py-3 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-mono text-slate-700">{c.id.slice(0, 8)}</div>
                    <div className="text-xs text-slate-500">{new Date(c.created_at).toLocaleString()}</div>
                  </div>
                  {(() => {
                    const rs = reviewState(c);
                    if (rs === "review") return <span className="pill-review">review needed</span>;
                    if (rs === "live") return <span className="pill-succeeded">live</span>;
                    const shown = (!isSuper && c.status === "degraded") ? "succeeded" : c.status;
                    return <span className={`pill-${shown}`}>{shown}</span>;
                  })()}
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}

const PLAN_SITES_MAX = 6;

/** Per-site "what to convert next" summary (first few sites). Each site's
 *  plan loads independently so one failing site doesn't blank the card. */
function PlanOverview({ sites }: { sites: Site[] }) {
  const [plans, setPlans] = useState<Record<string, PlanSummary | "error">>({});
  const shown = sites.slice(0, PLAN_SITES_MAX);
  const key = shown.map((s) => s.id).join(",");

  useEffect(() => {
    if (!key) return;
    let cancelled = false;
    const ids = key.split(",");
    Promise.allSettled(ids.map((id) => api.getPlan(id))).then((res) => {
      if (cancelled) return;
      const next: Record<string, PlanSummary | "error"> = {};
      res.forEach((r, i) => { next[ids[i]] = r.status === "fulfilled" ? r.value : "error"; });
      setPlans(next);
    });
    return () => { cancelled = true; };
  }, [key]);

  if (shown.length === 0) return null;
  const group = (p: PlanSummary, pr: Priority) => p.groups.find((g) => g.priority === pr);

  return (
    <section className="card mb-6" aria-labelledby="dash-plan-title">
      <div className="card-header">
        <h2 id="dash-plan-title" className="text-base font-semibold text-slate-800 flex items-center gap-2">
          <ListOrdered className="h-4 w-4 text-brand-600" aria-hidden="true" /> Conversion plan
        </h2>
        <Link to="/plan" className="text-sm text-brand-600 hover:underline">Open plan</Link>
      </div>
      <ul className="divide-y divide-slate-200">
        {shown.map((s) => {
          const p = plans[s.id];
          return (
            <li key={s.id} className="px-4 sm:px-5 py-3 flex flex-wrap items-center gap-x-6 gap-y-1">
              <div className="font-medium text-slate-800 w-full sm:w-auto sm:min-w-[180px] truncate">{s.domain}</div>
              {p === undefined ? (
                <span className="text-sm text-slate-500">Loading…</span>
              ) : p === "error" ? (
                <span className="text-sm text-slate-500">Plan not available yet</span>
              ) : (
                (["now", "next"] as Priority[]).map((pr) => {
                  const g = group(p, pr);
                  return (
                    <span key={pr} className="text-sm text-slate-600 inline-flex items-center gap-2">
                      <PriorityPill priority={pr} />
                      <strong className="text-ink tabular-nums">{g?.count ?? 0}</strong>
                      <span className="text-xs text-slate-500 tabular-nums">{(g?.pages ?? 0).toLocaleString()} pages</span>
                    </span>
                  );
                })
              )}
              <Link
                to="/plan"
                onClick={() => writePlanSite(s.id)}
                className="ml-auto text-sm text-brand-600 hover:underline"
                aria-label={`View conversion plan for ${s.domain}`}
              >
                View
              </Link>
            </li>
          );
        })}
      </ul>
      {sites.length > PLAN_SITES_MAX && (
        <div className="px-5 py-2 text-xs text-slate-500 border-t border-slate-200">
          Showing {PLAN_SITES_MAX} of {sites.length} sites — pick others on the plan page.
        </div>
      )}
    </section>
  );
}

function Stat({ icon: Icon, label, value, sub }: { icon: React.ComponentType<{ className?: string }>; label: string; value: number; sub?: string }) {
  return (
    <div className="card">
      <div className="card-body">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-sm text-slate-500">{label}</div>
            <div className="mt-1 text-2xl sm:text-3xl font-semibold text-slate-900 tabular-nums">{value}</div>
            {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
          </div>
          <div className="hidden sm:block rounded-md bg-brand-50 p-2"><Icon className="h-5 w-5 text-brand-600" /></div>
        </div>
      </div>
    </div>
  );
}
